import {
  isAndroid,
  isAndroidOrHarmony,
  isGameConsole,
  isIos,
  isMobile,
  isPc,
  isPcOrLinux, isWechatMiniProgram,
  PlatformUtil
} from 'utils/platform';
import i18n from 'i18n';
import uniq from 'lodash/array/uniq';
import { ProductSummaryUtil } from 'components/exception/ProductSummaryPage/ProductSummaryUtil';
import { IssueCrashFilterUtil } from 'components/commons/IssueCrashFilter/IssueCrashFilter';
import { isNullish } from 'utils/nullish';
import { VersatileKeywordSearchMethod } from 'components/commons/IssueCrashFilter/VersatileKeywordSearchMethod';
import { EnvUtil } from 'utils/env-util';
import { PLATFORM_ID } from 'utils/constants';

export const LogicalOperator = Object.freeze({
  AND: 'AND',
  OR: 'OR',
});

export const QueryType = Object.freeze({
  TERM: 'TERM',
  TERM_CONTAINS: 'TERM_CONTAINS',
  TERM_WILDCARD: 'TERM_WILDCARD',
  /** 指定的正则表达式必须能匹配到被搜索字段的部分内容。如果需要完全匹配，在表达式中使用锚点符号（^和$） */
  TERM_REGEXP_MATCH: 'TERM_REGEXP_MATCH',
  TERMS: 'TERMS',
  TERMS_WILDCARD: 'TERMS_WILDCARD',
  /** 基于ES的terms set query。要求被查询字段的值的list全部都要在查询条件的terms里面。（值为空list也算命中） */
  TERMS_SET_ALL_MATCH: 'TERMS_SET_ALL_MATCH',
  /** 旧版基于SearchBean时候搜索的text格式 */
  TEXT_LEGACY_SEARCH: 'TEXT_LEGACY_SEARCH',
  TEXT_MATCH_PHRASE: 'TEXT_MATCH_PHRASE',
  TEXT_SIMPLE_QUERY_STRING: 'TEXT_SIMPLE_QUERY_STRING',
  RANGE: 'RANGE',
  RANGE_RELATIVE_DATETIME: 'RANGE_RELATIVE_DATETIME',
  NONEMPTY: 'NONEMPTY',
  // 一些特殊字段不满足以上通用的过滤逻辑，value格式也是特殊的
  CUSTOM: 'CUSTOM',
});

export const QueryTypeUtil = Object.freeze({
  isTermLike(queryType) {
    return (queryType || '').startsWith(QueryType.TERM);
  },
  isTermsLike(queryType) {
    return (queryType || '').startsWith(QueryType.TERMS);
  },
  isTextLike(queryType) {
    return (queryType || '').startsWith('TEXT');
  },
});

/**
 * IssueCrashFilterExUtil：IssueCrashFilterEx 过滤器的字段元数据定义。
 *
 * - FieldName：所有可搜索字段的名称枚举。这个名称会作为搜索接口构造 searchConditionGroup 时的字段名，
 *   一旦定义要慎重取名（不能轻易改），尽量和 crashMap/esMap 对应字段同名，除非该字段名不适合公开。
 * - AllFilterFields：每个字段的基本信息（属于 issue 还是 crash/OOM 级字段、哪些平台可用、
 *   用哪种通用搜索方式渲染等），供 IssueCrashFilterEx / FieldValuePart 读取使用。
 */
export const FieldName = Object.freeze({
  version: 'version',
  issueUploadTime: 'issueUploadTime',
  crashUploadTime: 'crashUploadTime',
  crashTime: 'crashTime',
  issueFirstUploadTime: 'issueFirstUploadTime',
  issueFirstCrashVersion: 'issueFirstCrashVersion',
  exceptionType: 'exceptionType',
  exceptionCategory: 'exceptionCategory',
  issueTag: 'issueTag',
  issueStatus: 'issueStatus',
  issueAiStatus: 'issueAiStatus',
  tapdBugStatus: 'tapdBugStatus',
  jiraBugStatus: 'jiraBugStatus',
  processor: 'processor',
  issueReportCount: 'issueReportCount',
  issueDeviceCount: 'issueDeviceCount',
  bundleId: 'bundleId',
  channelId: 'channelId',
  /** 签名渠道。部分项目需要支持搜索此字段，由超管开关enableFilterCheckedChannel控制是否对用户开放 */
  checkedChannel: 'checkedChannel',
  vmStatus: 'vmStatus', // 已废弃，vmStatus只支持标记真机/模拟器，现在改用vmType，可以标记真机/模拟器/云游戏/PlayCover等
  vmType: 'vmType',
  vmDeviceBrand: 'vmDeviceBrand',
  appInBack: 'appInBack',
  isSecondReport: 'isSecondReport',
  /** 是否是网吧机器 */
  isNetbarMachine: 'isNetbarMachine',
  systemExitStatus: 'systemExitStatus',
  /** iOS MetricKit：是否启动崩溃（ES: reservedMapFlattened.startupCrash） */
  startupCrash: 'startupCrash',
  /** iOS MetricKit 卡死：是否启动卡死（ES: reservedMapFlattened.startupHang） */
  startupHang: 'startupHang',
  hasHackedModule: 'hasHackedModule',
  issueId: 'issueId',
  crashId: 'crashId',
  expUid: 'expUid',
  /** 异常名（Issue+Crash混用）。已废弃 */
  errorType: 'errorType',
  /** 异常名（仅Crash）。因为有很多项目的异常名不参与分类，issue和crash的异常名不一致，所以高级搜索改用这个字段 */
  crashExpName: 'crashExpName',
  crashExpMessage: 'crashExpMessage',
  /** GPU Breadcrumbs（之前叫GPU堆栈，ES里面字段是customCrashStack） */
  gpuBreadcrumbs: 'gpuBreadcrumbs',
  crashInfos: 'crashInfos',
  model: 'model', // 以前叫hardware
  appBit: 'appBit',
  iosModel: 'iosModel', // 以前叫modelList
  androidModel: 'androidModel',
  osVersion: 'osVersion',
  processName: 'processName',
  threadName: 'threadName',
  userSceneTag: 'userSceneTag',
  elapsedTime: 'elapsedTime',
  /** 启动时间的时间戳，单位是毫秒 */
  startMillis: 'startMillis',
  ramGib: 'ramGib',
  rom: 'rom',
  ram: 'ram', // 以前叫ramRange
  cpu: 'cpu',
  soc: 'soc',
  cpuArch: 'cpuArch',
  gpu: 'gpu',
  gpuDriverVersion: 'gpuDriverVersion',
  countryOrRegion: 'countryOrRegion', // 以前叫country
  userId: 'userId', // 以前叫contectInfo
  /** 根据用户ID备注搜索用户，备注的字段放在subField里面。界面上用来区分不同搜索字段的，实际上跟userId一样。 */
  userIdByAlias: 'userIdByAlias',
  deviceId: 'deviceId', // 以前叫deviceIdList
  customKey: 'customKey',
  keyStackSoName: 'keyStackSoName',
  appSoNameWhitelist: 'appSoNameWhitelist', // iGame定制需求字段
  customLog: 'customLog',
  systemLog: 'systemLog',
  originalCrashStack: 'originalCrashStack',
  crashDetail: 'crashDetail', // 以前叫detail
  oomStatus: 'oomStatus',
  featureTag: 'featureTag',
  jankAvgTime: 'jankAvgTime',
  /** 当前存的是clickhouse的sub module id的内容 */
  serverEnv: 'serverEnv',

  /** 更多线程（存在额外的ES集群里面） */
  otherThreadStack: 'otherThreadStack',
  /** print log（存在额外的ES集群里面） */
  printLog: 'printLog',

  /** 先锋云游戏特有字段：平台类型 */
  gmHardware: 'gmHardware',
  /** 先锋云游戏特有字段：平台集群 */
  gmCluster: 'gmCluster',
  /** 先锋云游戏特有字段：Game ID */
  gmGameId: 'gmGameId',
  /** 先锋云游戏特有字段：游戏集群 */
  gmGameCluster: 'gmGameCluster',
});

export const FieldCategory = Object.freeze({
  ISSUE: 'ISSUE',
  CRASH: 'CRASH',
  OOM: 'OOM',
  /** 更多线程、PrintLog等单独存储在其他ES集群的上报字段 */
  CRASH_EXTRA: 'CRASH_EXTRA',
});

export const CaseSensitivity = Object.freeze({
  SENSITIVE: 'SENSITIVE',
  INSENSITIVE: 'INSENSITIVE',
});

/**
 * @type {{ name: string, fieldCategoryList: string[], availableByPid: function, issueOnly: boolean }[]}
 * */
export const AllFilterFields = [{
  name: FieldName.version,
  fieldCategoryList: [FieldCategory.ISSUE, FieldCategory.CRASH, FieldCategory.OOM],
  // 支持两条版本条件组合出 between（例如 version >= A 且 version < B）
  allowMultipleConditions: true,
}, {
  name: FieldName.issueUploadTime,
  fieldCategoryList: [FieldCategory.ISSUE],
}, {
  name: FieldName.crashUploadTime,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
}, {
  name: FieldName.crashTime,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
}, {
  name: FieldName.issueFirstUploadTime,
  fieldCategoryList: [FieldCategory.ISSUE],
}, {
  name: FieldName.issueFirstCrashVersion,
  fieldCategoryList: [FieldCategory.ISSUE],
}, {
  name: FieldName.exceptionType,
  fieldCategoryList: [FieldCategory.ISSUE, FieldCategory.CRASH],
  availableByPid: pid => PlatformUtil.isExceptionTypeSelectSupported(pid),
  isMultipleSelect: true,
  placeholder: i18n.t('exceptionTypeOptions.所有类型'),
  filterOptionProperty: 'exceptionTypeList',
},  {
  name: FieldName.exceptionCategory,
  fieldCategoryList: [FieldCategory.ISSUE, FieldCategory.CRASH],
  isMultipleSelect: true,
  placeholder: i18n.t('exceptionTypeOptions.所有类型'),
}, {
  name: FieldName.issueTag,
  fieldCategoryList: [FieldCategory.ISSUE],
  isMultipleSelect: true,
  placeholder: i18n.t('tagOptions.所有标签'),
  filterOptionProperty: 'tagList',
}, {
  name: FieldName.issueStatus,
  fieldCategoryList: [FieldCategory.ISSUE],
  isMultipleSelect: true,
  placeholder: i18n.t('statusOptions.所有状态'),
  filterOptionProperty: 'status',
}, {
  name: FieldName.issueAiStatus,
  fieldCategoryList: [FieldCategory.ISSUE],
} ,{
  name: FieldName.tapdBugStatus,
  fieldCategoryList: [FieldCategory.ISSUE],
  placeholder: i18n.t('tapdBugStatusOptions.所有TAPD缺陷关联状态'),
}, {
  name: FieldName.jiraBugStatus,
  fieldCategoryList: [FieldCategory.ISSUE],
  placeholder: i18n.t('tapdBugStatusOptions.所有TAPD缺陷关联状态'),
}, {
  name: FieldName.processor,
  fieldCategoryList: [FieldCategory.ISSUE],
  placeholder: i18n.t('processorOptions.所有处理人'),
}, {
  name: FieldName.issueDeviceCount,
  fieldCategoryList: [FieldCategory.ISSUE],
}, {
  name: FieldName.issueReportCount,
  fieldCategoryList: [FieldCategory.ISSUE],
}, {
  name: FieldName.bundleId,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isMobile(pid),
  placeholder: i18n.t('bundleOptions.所有应用包名'),
}, {
  name: FieldName.channelId,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isMobile(pid),
  placeholder: i18n.t('channelOptions.全渠道'),
}, {
  // 签名渠道。部分项目需要支持搜索此字段，按照VersatileKeywordSearch方式渲染搜索内容部分。
  // 是否可见由开关enableFilterCheckedChannel控制（IssueCrashFilterEx里面处理）
  name: FieldName.checkedChannel,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.issueId,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
}, {
  name: FieldName.expUid,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
}, {
  name: FieldName.userId,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
}, {
  name: FieldName.userIdByAlias,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
}, {
  name: FieldName.deviceId,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => !isGameConsole(pid),
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
}, {
  name: FieldName.errorType,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.crashExpName,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.crashExpMessage,
  fieldCategoryList: [FieldCategory.CRASH],
  allowMultipleConditions: true,
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.androidModel,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isAndroidOrHarmony(pid),
}, {
  name: FieldName.model,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isMobile(pid) || PlatformUtil.isMac(pid) || isPc(pid, undefined) || isWechatMiniProgram(pid),
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.appBit,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isAndroidOrHarmony(pid),
}, {
  name: FieldName.iosModel,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isIos(pid),
}, {
  name: FieldName.osVersion,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.processName,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.threadName,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.elapsedTime,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.startMillis,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.vmStatus,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isAndroid(pid),
}, {
  name: FieldName.vmType,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isAndroid(pid),
},  {
  name: FieldName.appInBack,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isMobile(pid),
}, {
  name: FieldName.isSecondReport,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => PlatformUtil.isMobileOrMobileLike(pid),
}, {
  name: FieldName.isNetbarMachine,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isPc(pid, undefined),
}, {
  name: FieldName.systemExitStatus,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isMobile(pid),
}, {
  name: FieldName.startupCrash,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isIos(pid),
}, {
  name: FieldName.startupHang,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isIos(pid),
}, {
  name: FieldName.countryOrRegion,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => !EnvUtil.isChinaEnv(),
} ,{
  name: FieldName.ram,
  fieldCategoryList: [FieldCategory.CRASH],
  allowMultipleConditions: true,
  availableByPid: pid => isAndroid(pid) || isIos(pid) || isPc(pid, undefined),
}, {
  name: FieldName.ramGib,
  fieldCategoryList: [FieldCategory.OOM],
}, {
  name: FieldName.rom,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isAndroidOrHarmony(pid),
  isVersatileKeywordSearch: true,
  searchMethodWhitelist: [
    VersatileKeywordSearchMethod.CONTAINS,
    VersatileKeywordSearchMethod.NOT_CONTAINS,
  ],
}, {
  name: FieldName.cpu,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => PlatformUtil.isMac(pid) || PlatformUtil.is(pid, PLATFORM_ID.PC),
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.cpuArch,
  fieldCategoryList: [FieldCategory.CRASH, FieldCategory.OOM],
  availableByPid: pid => isAndroid(pid),
}, {
  name: FieldName.gpu,
  fieldCategoryList: [FieldCategory.CRASH],
  // PC和安卓均已全量开放。安卓端曾经需要通过enableAndroidSearchGpu开关才放开（判断逻辑在IssueCrashFilterEx里面），现已移除该限制。
  availableByPid: pid => PlatformUtil.is(pid, PLATFORM_ID.ANDROID) || PlatformUtil.is(pid, PLATFORM_ID.PC),
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.gpuDriverVersion,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isPc(pid, undefined),
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.customKey,
  fieldCategoryList: [FieldCategory.CRASH],
  allowMultipleConditions: true,
}, {
  name: FieldName.keyStackSoName,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
}, {
  name: FieldName.appSoNameWhitelist,
  fieldCategoryList: [FieldCategory.CRASH],
  defaultValue: { queryType: QueryType.TERMS_SET_ALL_MATCH, not: true },
}, {
  name: FieldName.crashDetail,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileTextSearch: true,
}, {
  name: FieldName.originalCrashStack,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isMobile(pid),
  isVersatileTextSearch: true,
}, {
  name: FieldName.crashInfos,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileTextSearch: true,
}, {
  name: FieldName.gpuBreadcrumbs,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isPc(pid, undefined),
  isVersatileTextSearch: true,
}, /* {  // 目前不支持搜索systemLog和customLog
  name: FieldName.systemLog,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileTextSearch: true,
}, {
  name: FieldName.customLog,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileTextSearch: true,
},*/ {
  name: FieldName.oomStatus,
  fieldCategoryList: [FieldCategory.OOM],
}, {
  name: FieldName.userSceneTag,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
  hasIsEmptyQueryType: true,
}, {
  name: FieldName.hasHackedModule,
  fieldCategoryList: [FieldCategory.CRASH],
  availableByPid: pid => isAndroid(pid),
}, {
  name: FieldName.serverEnv,
  fieldCategoryList: [FieldCategory.CRASH],
  isVersatileKeywordSearch: true,
  hasIsEmptyQueryType: true,
}, {
  name: FieldName.featureTag,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.crashId,
  fieldCategoryList: [FieldCategory.CRASH],
  customLabel: 'Crash ID',
  isVersatileKeywordSearch: true,
  defaultQueryType: QueryType.TERM,
}, {
  name: FieldName.printLog,
  fieldCategoryList: [FieldCategory.CRASH_EXTRA],
  isVersatileTextSearch: true,
}, {
  name: FieldName.gmHardware,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.gmCluster,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.gmGameId,
  fieldCategoryList: [FieldCategory.CRASH],
}, {
  name: FieldName.gmGameCluster,
  fieldCategoryList: [FieldCategory.CRASH],
}].map(x => {
  return {
    ...x,
    issueOnly: x.fieldCategoryList.length === 1 && x.fieldCategoryList.includes(FieldCategory.ISSUE),
    oomOnly: x.fieldCategoryList.length === 1 && x.fieldCategoryList.includes(FieldCategory.OOM),
    crashExtraOnly: x.fieldCategoryList.length === 1 && x.fieldCategoryList.includes(FieldCategory.CRASH_EXTRA),
  };
});

export const FieldNameToFieldInfo = Object.freeze(Object.assign(Object.create(null), ...AllFilterFields.map(x => ({ [x.name]: x }))));




export const IssueCrashFilterExUtil = (function() {
  return Object.freeze({
    makeSearchConditionGroupFromSearchParamsDict(searchParamsDict) {
      if (!searchParamsDict) {
        return {};
      }
      let searchConditionGroup = searchParamsDict.searchConditionGroup || {};

      // 兼容旧的IssueCrashFilter的数据结构
      const filterData = IssueCrashFilterUtil.makeFilterDataFromQueryDict(searchParamsDict, true, true);
      const conditionsFromFilterData = IssueCrashFilterUtil.makeSearchConditionGroupConditionsFromFilterData(filterData);

      return {
        ...searchConditionGroup,
        conditions: [...(searchConditionGroup.conditions || []), ...conditionsFromFilterData],
      };
    },

    makeSearchParamsDictFromSearchConditionGroup(searchConditionGroup) {
      if (!searchConditionGroup) {
        return {};
      }

      const legacyFilterData = (searchConditionGroup.conditions || [])
        .filter(x => (x.field || '').startsWith('LEGACY_'))
        .map(x => {
          return { name: x.field.replace('LEGACY_', ''), value: x.legacyValue };
        });

      let legacyParamsDict = IssueCrashFilterUtil.makeQueryDictFromFilterData(legacyFilterData);
      for (let k in legacyParamsDict) { // 移除掉nullish和空字符串的值
        if (isNullish(legacyParamsDict[k]) || legacyParamsDict[k] === '') {
          delete legacyParamsDict[k];
        }
      }

      const conditionsWithoutLegacy = (searchConditionGroup.conditions || [])
        .filter(x => !(x.field || '').startsWith('LEGACY_'));

      let finalSearchConditionGroup = {
        ...searchConditionGroup,
        conditions: conditionsWithoutLegacy,
      };
      if (finalSearchConditionGroup.conditions.length === 0
        && (finalSearchConditionGroup.subgroups || []).length === 0) {
        finalSearchConditionGroup = null;
      }

      return {
        ...(finalSearchConditionGroup ? { searchConditionGroup: finalSearchConditionGroup } : {}),
        ...legacyParamsDict,
      }
    },

    /** 从一个scg中提取出所有涉及到的字段。（会递归拿取子scg的字段） */
    getAllFieldsFromSearchConditionGroup(searchConditionGroup) {
      const { conditions, subgroups } = searchConditionGroup || {};
      const shallowFields = (conditions || []).map(x => x.field);
      const deepFields = subgroups && subgroups.length > 0
        ? subgroups.map(x => this.getAllFieldsFromSearchConditionGroup(x)).flat()
        : [];
      return uniq([...shallowFields, ...deepFields]);
    },

    /** 根据app id判断哪些项目支持下拉选择bundleId。有的项目bundleId脏数据太多，获取选项界面会卡死，就不支持下拉了 */
    getIsBundleIdOptionsAvailable(appId) {
      const blacklist = process.env.REACT_APP_DISABLE_BUNDLE_ID_OPTIONS_APPS || [];
      return !blacklist.includes(appId);
    },
  });
})();
